import { Fragment } from 'react';
import { LuChevronDown, LuInfo, LuMinus, LuPlus, LuRotateCcw } from 'react-icons/lu';

import { PortfolioEngineAccount } from '@ndvr/portfolio-api';

import { ExcludedPosition, InferredPosition, PortfolioInferredPositions } from '@/cip/_openapi/portfolio-optimizer';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { EllipsisTooltip } from '@/components/ui/EllipsisTooltip';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { WeightInput } from '@/components/ui/WeightInput';
import { calculateTotalWeight } from '@/views/portfolio-engine/allocation-builder/shared-utils';
import { formatCurrency } from 'utils/number-formatter';

import { OnboardingStepProps } from '../types';
import { buildWeightMap, positionKey } from '../utils';

export const TargetAllocationStep = ({
    accounts,
    inferredPositions,
    isInferredPositionsLoading,
    state,
    setState,
}: OnboardingStepProps) => {
    const byGuid = new Map(inferredPositions.map((p) => [p.portfolioGuid, p]));

    const handleWeightChange = (accountGUID: string, key: string, value: string) => {
        setState((prev) => ({
            ...prev,
            weightOverrides: {
                ...prev.weightOverrides,
                [accountGUID]: {
                    ...prev.weightOverrides[accountGUID],
                    [key]: value,
                },
            },
        }));
    };

    const handleReset = (accountGUID: string) => {
        const portfolio = inferredPositions.find((p) => p.portfolioGuid === accountGUID);
        if (!portfolio) return;
        setState((prev) => ({
            ...prev,
            weightOverrides: { ...prev.weightOverrides, [accountGUID]: buildWeightMap(portfolio) },
        }));
    };

    return (
        <div className="flex flex-col gap-6">
            <div>
                <h3 className="text-cocoa-1000 text-2xl font-medium">Target Allocation</h3>
                <p className="text-cocoa-600 mt-1 text-sm">
                    Derive the target allocation for each account based on its current holding structure.
                </p>
            </div>

            {isInferredPositionsLoading ? (
                <div className="text-cocoa-500 text-sm">Computing inferred allocations…</div>
            ) : (
                <ul className="divide-cocoa-200 divide-y">
                    {accounts.map((account, i) => (
                        <li key={account.accountGUID}>
                            <AccountAllocationRow
                                account={account}
                                positions={byGuid.get(account.accountGUID)}
                                overrides={state.weightOverrides[account.accountGUID] ?? {}}
                                onWeightChange={(key, value) => handleWeightChange(account.accountGUID, key, value)}
                                onReset={() => handleReset(account.accountGUID)}
                                defaultOpen={i === 0}
                            />
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
};

const AccountAllocationRow = ({
    account,
    positions,
    overrides,
    onWeightChange,
    onReset,
    defaultOpen = false,
}: {
    account: PortfolioEngineAccount;
    positions: PortfolioInferredPositions | undefined;
    overrides: Record<string, string>;
    onWeightChange: (key: string, value: string) => void;
    onReset: () => void;
    defaultOpen?: boolean;
}) => {
    const included = positions?.positions ?? [];
    const excluded = positions?.excludedPositions ?? [];
    const cash = positions?.cash ?? 0;
    const targetTotal = included.reduce((sum, p) => sum + p.currentExposure, 0) + cash;
    const excludedTotal = excluded.reduce((sum, p) => sum + p.currentExposure, 0);
    const grandTotal = targetTotal + excludedTotal;
    const targetShare = grandTotal > 0 ? targetTotal / grandTotal : 1;

    return (
        <Collapsible className="group/row" defaultOpen={defaultOpen}>
            <CollapsibleTrigger className="hover:bg-cocoa-50 flex w-full items-center gap-4 px-2 py-3 text-left transition">
                <LuChevronDown className="text-cocoa-500 h-4 w-4 shrink-0 transition-transform group-data-[state=open]/row:rotate-180" />
                <span className="text-cocoa-1000 min-w-0 flex-1 truncate text-sm font-medium">
                    {account.accountNamePlain ?? account.accountGUID}
                </span>
                <div className="bg-cocoa-100 relative h-2 flex-1 overflow-hidden rounded-full">
                    <div
                        className="bg-gold-500 h-full rounded-full transition-[width]"
                        style={{ width: `${Math.round(targetShare * 100)}%` }}
                    />
                </div>
            </CollapsibleTrigger>
            <CollapsibleContent>
                <div className="grid grid-cols-1 gap-8 px-8 py-4 md:grid-cols-2">
                    <AllocationsTable
                        positions={included}
                        cash={cash}
                        overrides={overrides}
                        onWeightChange={onWeightChange}
                        onReset={onReset}
                    />
                    <ExclusionsTable exclusions={excluded} total={excludedTotal} />
                </div>
            </CollapsibleContent>
        </Collapsible>
    );
};

const AllocationsTable = ({
    positions,
    cash,
    overrides,
    onWeightChange,
    onReset,
}: {
    positions: InferredPosition[];
    cash: number;
    overrides: Record<string, string>;
    onWeightChange: (key: string, value: string) => void;
    onReset: () => void;
}) => {
    if (positions.length === 0 && cash === 0) {
        return (
            <div>
                <SectionHeader title="Target Allocation" />
                <p className="text-cocoa-500 mt-3 text-sm italic">No positions to allocate.</p>
            </div>
        );
    }

    const sorted = [...positions].sort((a, b) => b.weight - a.weight);
    const weightItems = sorted.map((p) => ({ weight: overrides[positionKey(p)] ?? '0' }));
    const totalWeight = calculateTotalWeight(weightItems);
    const isValid = totalWeight === 100;

    const stepWeight = (key: string, delta: number) => {
        const current = parseFloat(overrides[key] ?? '0') || 0;
        const next = Math.round((current + delta) * 100) / 100;
        onWeightChange(key, String(Math.min(100, Math.max(0, next))));
    };

    return (
        <div>
            <SectionHeader title="Target Allocation" onReset={onReset} />
            <div className="mt-3 grid grid-cols-[4rem_1fr_auto] items-center gap-x-4">
                {/* Column headers */}
                <div className="text-cocoa-500 border-cocoa-200 border-b pb-2 text-xs font-semibold tracking-wider uppercase">
                    Ticker
                </div>
                <div className="text-cocoa-500 border-cocoa-200 border-b pb-2 text-xs font-semibold tracking-wider uppercase">
                    Fund Name
                </div>
                <div
                    className={`border-cocoa-200 flex items-center gap-1 border-b pb-2 text-xs font-semibold tracking-wider uppercase ${isValid ? 'text-cocoa-500' : 'text-red-600'}`}
                >
                    {totalWeight}%
                    <TooltipProvider>
                        <Tooltip>
                            <TooltipTrigger asChild>
                                <span className="cursor-default">
                                    <LuInfo className="h-3 w-3 shrink-0" />
                                </span>
                            </TooltipTrigger>
                            <TooltipContent>Weights must sum to 100%</TooltipContent>
                        </Tooltip>
                    </TooltipProvider>
                </div>

                {/* Position rows */}
                {sorted.map((row) => {
                    const key = positionKey(row);
                    return (
                        <Fragment key={key}>
                            <EllipsisTooltip className="border-cocoa-100 text-cocoa-700 border-b py-2.5 text-sm font-semibold">
                                {row.symbol ?? '—'}
                            </EllipsisTooltip>
                            <EllipsisTooltip className="border-cocoa-100 text-cocoa-700 border-b py-2.5 text-sm uppercase">
                                {row.description}
                            </EllipsisTooltip>
                            <div className="border-cocoa-100 flex items-center gap-1 border-b py-2.5">
                                <WeightInput
                                    value={overrides[key] ?? ''}
                                    onChange={(value) => onWeightChange(key, value)}
                                    error={!isValid && overrides[key] !== undefined}
                                />
                                <button
                                    type="button"
                                    onClick={() => stepWeight(key, -1)}
                                    className="border-cocoa-200 text-cocoa-500 hover:bg-cocoa-50 flex h-6 w-6 shrink-0 cursor-pointer items-center justify-center self-center rounded-full border transition"
                                >
                                    <LuMinus className="h-3 w-3" />
                                </button>
                                <button
                                    type="button"
                                    onClick={() => stepWeight(key, 1)}
                                    className="border-cocoa-200 text-cocoa-500 hover:bg-cocoa-50 flex h-6 w-6 shrink-0 cursor-pointer items-center justify-center self-center rounded-full border transition"
                                >
                                    <LuPlus className="h-3 w-3" />
                                </button>
                            </div>
                        </Fragment>
                    );
                })}

                {cash > 0 && (
                    <Fragment key="cash">
                        <div className="text-cocoa-700 py-2.5 text-sm italic">Cash</div>
                        <div className="py-2.5" />
                        <div className="text-cocoa-500 py-2.5 text-sm">—</div>
                    </Fragment>
                )}
            </div>
        </div>
    );
};

const ExclusionsTable = ({ exclusions, total }: { exclusions: ExcludedPosition[]; total: number }) => {
    if (exclusions.length === 0) {
        return (
            <div>
                <SectionHeader title="Excluded Holdings" />
                <p className="text-cocoa-500 mt-3 text-sm italic">No excluded holdings.</p>
            </div>
        );
    }
    return (
        <div>
            <SectionHeader title="Excluded Holdings" trailing={`${formatCurrency(total)} total`} />
            <div className="mt-3 grid grid-cols-[1fr_auto] gap-x-6 gap-y-2 text-sm">
                {exclusions.map((row) => (
                    <div className="contents" key={`${row.sId}-${row.scId ?? ''}`}>
                        <div className="flex items-baseline gap-2 truncate">
                            <span className="text-cocoa-1000 font-medium">{row.symbol ?? '—'}</span>
                            <span className="text-cocoa-600 truncate">{row.description}</span>
                        </div>
                        <div className="text-cocoa-1000 text-right tabular-nums">
                            {formatCurrency(row.currentExposure)}
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};

const SectionHeader = ({ title, trailing, onReset }: { title: string; trailing?: string; onReset?: () => void }) => (
    <div className="border-cocoa-200 flex items-center justify-between border-b pb-1">
        <span className="text-cocoa-500 text-xs font-medium tracking-wider uppercase">{title}</span>
        <div className="flex items-center gap-3">
            {trailing && <span className="text-cocoa-500 text-xs tabular-nums">{trailing}</span>}
            {onReset && (
                <button
                    type="button"
                    onClick={onReset}
                    className="text-cocoa-600 hover:text-cocoa-900 flex cursor-pointer items-center gap-1 text-xs transition"
                >
                    <LuRotateCcw className="h-3 w-3" />
                    Reset
                </button>
            )}
        </div>
    </div>
);
